import React, { useState, useContext, useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
    FormControl, RadioGroup, FormControlLabel, Radio, Button, Stack,
    TextField, Card, CardContent, CardHeader, Grid, Typography, Autocomplete, Checkbox, FormGroup, Box
} from '@mui/material';
import authContext from '../../context/auth-context';
import { postFetchAllSalesPromotion } from '../../contractManagement/thunks/postFetchAllSalesPromotion';
import { updateNewSalesPromotion } from '../../contractManagement/thunks/updateNewSalesPromotion';
import {
    fetchAirlinePromotionById,
    createAirlinePromotion,
    updateAirlinePromotion,
    deleteAirlinePromotion,
} from '../../contractManagement/thunks/airlinePromotionCode';
import swal from 'sweetalert';
import "./style.css";

const formatApiDate = (value) => (value ? String(value).split('T')[0] : '');

const removeEmptyStrings = (obj) => {
    const cleaned = { ...obj };
    Object.keys(cleaned).forEach((key) => {
        if (cleaned[key] === '' || cleaned[key] === null || cleaned[key] === undefined) {
            delete cleaned[key];
        }
    });
    return cleaned;
};

const TITLE_BY_TYPE = {
    loyalty: 'Loyalty Promotion',
    sales: 'Sales Promotion',
    airline: 'Airline Promotion',
};

const ROUTE_BY_TYPE = {
    loyalty: '/promotions/loyalty',
    sales: '/promotions/sales',
    airline: '/promotions/airline',
};

const resolvePromotionType = (promotionTypeProp, isLoyaltyPromotionProp, pathname) => {
    if (promotionTypeProp) return promotionTypeProp;
    if (isLoyaltyPromotionProp === true) return 'loyalty';
    if (isLoyaltyPromotionProp === false) return 'sales';
    if (pathname.includes('/promotions/airline')) return 'airline';
    if (pathname.includes('/promotions/loyalty') || pathname.includes('/newLoyaltyPromotions')) return 'loyalty';
    if (pathname.includes('/promotions/sales') || pathname.includes('/SalesEdit')) return 'sales';
    return 'sales';
};

function PromotionEdit({ promotionType: promotionTypeProp, isLoyaltyPromotion: isLoyaltyPromotionProp }) {
    const location = useLocation();
    const history = useHistory();
    const { id } = useParams();
    const promotionType = resolvePromotionType(promotionTypeProp, isLoyaltyPromotionProp, location.pathname);
    const isLoyalty = promotionType === 'loyalty';
    const isSales = promotionType === 'sales';
    const isAirline = promotionType === 'airline';
    const isLoyaltyPromotion = isLoyalty;
    const { orgId } = useContext(authContext);
    const [consortiumValues, setConsortiumValues] = useState([]);
    const isEdit = !!id;
    const salesNaID = id;

    const { control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm({
        defaultValues: {
            restriction: 'no_restriction',
            reservationDateFrom: '',
            reservationDateTo: '',
            departureRestriction: 'no_restriction',
            departureDateFrom: '',
            departureDateTo: '',
            consortiumType: '',
            consortium: null,
            agencyType: '',
            agency: '',
            agencyLocationType: '',
            agencyLocation: '',
            agentType: '',
            agent: '',
            bookingChannel: [],
            supplier: [],
            originType: isAirline ? 'include' : '',
            origin: '',
            destinationType: isAirline ? 'include' : '',
            destination: '',
            brandCodeType: '',
            brandCode: '',
            airlineType: isAirline ? 'include' : '',
            airline: '',
            tripTypes: [],
            cabinTypes: [],
            voidAllowed: false,
            refundAllowed: false,
            reissueAllowed: false,
            ticketingFee: '',
            voidFee: '',
            refundFee: '',
            reissueFee: '',
            bookingFee: '',
            merchantFee: '',
            pubCommissionAmount: '',
            sellPubAtCost: false,
            netLowestOfThisOrMarkup: '',
            netDefaultMarkup: '',
            promotionCode: '',
            loyaltyPoints: '',
            overrideNoProfitNoPoints: false
        },
    });

    const normalizeField = (val, returnAsArray = false) => {
        if (typeof val === 'string') {
            const trimmed = val.trim();

            if (!trimmed) return returnAsArray ? [] : '';
            if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                try {
                    const sanitized = trimmed.replace(/\[([^\[\]"]+)\]/g, (match, inner) => {
                        const items = inner.split(',').map(i => `"${i.trim()}"`);
                        return `[${items.join(',')}]`;
                    });
                    const parsed = JSON.parse(sanitized);
                    if (Array.isArray(parsed)) {
                        return returnAsArray ? parsed : parsed.join(', ');
                    }
                } catch {
                    return returnAsArray ? [trimmed] : trimmed;
                }
            }
            return returnAsArray ? [trimmed] : trimmed;
        }

        if (Array.isArray(val)) {
            return returnAsArray ? val : val.join(', ');
        }

        if (typeof val === 'number' || typeof val === 'boolean') {
            return returnAsArray ? [String(val)] : String(val);
        }

        return returnAsArray ? [] : '';
    };

    const getInclusionType = (inclusion, exclusion) => {
        const hasValue = (val) => {
            const norm = normalizeField(val, true);
            return norm.length > 0;
        };
        if (hasValue(inclusion)) return 'include';
        if (hasValue(exclusion)) return 'exclude';
        return '';
    };

    const mapApiToForm = (data) => {
        return {
            restriction: (data.ReservationDateFrom && data.ReservationDateTo) ? 'specific_duration' : 'no_restriction',
            reservationDateFrom: formatApiDate(data.ReservationDateFrom),
            reservationDateTo: formatApiDate(data.ReservationDateTo),
            departureRestriction: (data.DepartureDateFrom && data.DepartureDateTo) ? 'specific_duration' : 'no_restriction',
            departureDateFrom: formatApiDate(data.DepartureDateFrom),
            departureDateTo: formatApiDate(data.DepartureDateTo),

            consortiumType: getInclusionType(data.ConsortiumInclusion, data.ConsortiumExclusion),
            consortium: normalizeField(data.ConsortiumInclusion || data.ConsortiumExclusion),

            agencyType: getInclusionType(data.AgencyInclusion, data.AgencyExclusion),
            agency: normalizeField(data.AgencyInclusion || data.AgencyExclusion),

            agencyLocationType: getInclusionType(data.AgencyLocationInclusion, data.AgencyLocationExclusion),
            agencyLocation: normalizeField(data.AgencyLocationInclusion || data.AgencyLocationExclusion),

            agentType: getInclusionType(data.AgentInclusion, data.AgentExclusion),
            agent: normalizeField(data.AgentInclusion || data.AgentExclusion),

            bookingChannel: normalizeField(data.BookingChannelInclusion, true),
            supplier: normalizeField(data.SupplierInclusion, true),

            originType: isAirline ? 'include' : getInclusionType(data.OriginInclusion, data.OriginExclusion),
            origin: isAirline
                ? normalizeField(data.OriginInclusion)
                : normalizeField(data.OriginInclusion || data.OriginExclusion),

            destinationType: isAirline ? 'include' : getInclusionType(data.DestinationInclusion, data.DestinationExclusion),
            destination: isAirline
                ? normalizeField(data.DestinationInclusion)
                : normalizeField(
                    getInclusionType(data.DestinationInclusion, data.DestinationExclusion) === 'exclude'
                        ? data.DestinationExclusion
                        : data.DestinationInclusion
                ),

            brandCodeType: getInclusionType(data.BrandedFareInclusion, data.BrandedFareExclusion),
            brandCode: normalizeField(data.BrandedFareInclusion || data.BrandedFareExclusion),

            airlineType: isAirline ? 'include' : getInclusionType(data.AirlineInclusion, data.AirlineExclusion),
            airline: isAirline
                ? normalizeField(data.AirlineInclusion)
                : normalizeField(
                    getInclusionType(data.AirlineInclusion, data.AirlineExclusion) === 'exclude'
                        ? data.AirlineExclusion
                        : data.AirlineInclusion
                ),
            tripTypes: normalizeField(data.TripTypeInclusion, true),
            cabinTypes: normalizeField(data.CabinType, true),

            voidAllowed: data.AdAllowCancellation ?? false,
            refundAllowed: data.AdAllowRefund ?? false,
            reissueAllowed: data.AdAllowReissue ?? false,

            ticketingFee: normalizeField(data.TicketingFees),
            voidFee: normalizeField(data.VoidFees),
            refundFee: normalizeField(data.RefundFees),
            reissueFee: normalizeField(data.ReissueFees),
            bookingFee: normalizeField(data.BookingFees),
            merchantFee: normalizeField(data.MerchantFees),

            pubCommissionAmount: normalizeField(data.AddorRemovePUBCommissionAmount),
            sellPubAtCost: data.SellPUBAtCost ?? false,
            netLowestOfThisOrMarkup: normalizeField(data.LowestofDefaultMarkuporthisvalue),
            netDefaultMarkup: normalizeField(data.AddorRemoveFromDefaultMarkup),
            promotionCode: normalizeField(data.PromotionCode),
            loyaltyPoints: normalizeField(data.LoyaltyPoints ?? data.loyaltyPoints),
            ...(isLoyalty ? { overrideNoProfitNoPoints: data.OverrideNoProfitNoPoints ?? false } : {}),
        };
    };


    const mapFormToApiPayload = (form) => {
        if (isAirline) {
            return {
                reservationDateFrom: form.restriction === 'specific_duration' ? form.reservationDateFrom || '' : '',
                reservationDateTo: form.restriction === 'specific_duration' ? form.reservationDateTo || '' : '',
                departureDateFrom: form.departureRestriction === 'specific_duration' ? form.departureDateFrom || '' : '',
                departureDateTo: form.departureRestriction === 'specific_duration' ? form.departureDateTo || '' : '',
                originInclusion: form.origin?.toUpperCase() || '',
                originExclusion: '',
                destinationInclusion: form.destination?.toUpperCase() || '',
                destinationExclusion: '',
                brandedFareInclusion: '',
                brandedFareExclusion: '',
                airlineInclusion: form.airline?.toUpperCase() || '',
                airlineExclusion: '',
                bookingChannelInclusion: '',
                bookingChannelExclusion: '',
                supplierInclusion: form.supplier?.length ? JSON.stringify(form.supplier) : '',
                supplierExclusion: '',
                tripTypeInclusion: form.tripTypes?.length ? JSON.stringify(form.tripTypes) : '',
                tripTypeExclusion: '',
                cabinType: form.cabinTypes?.length ? JSON.stringify(form.cabinTypes) : '',
                promotionCode: form.promotionCode || '',
                loyaltyPoints: null,
            };
        }

        const consortiumName =
            consortiumValues.find((c) => c.consortiumId === form.consortium)?.consortiumName || '';

        const payload = {
            reservationDateFrom: form.restriction === 'specific_duration' ? form.reservationDateFrom || '' : '',
            reservationDateTo: form.restriction === 'specific_duration' ? form.reservationDateTo || '' : '',
            departureDateFrom: form.departureRestriction === 'specific_duration' ? form.departureDateFrom || '' : '',
            departureDateTo: form.departureRestriction === 'specific_duration' ? form.departureDateTo || '' : '',
            consortiumInclusion: form.consortiumType === 'include' ? form.consortium || '' : '',
            consortiumExclusion: form.consortiumType === 'exclude' ? form.consortium || '' : '',
            consortiumNameInclusion: form.consortiumType === 'include' ? consortiumName : '',
            consortiumNameExclusion: form.consortiumType === 'exclude' ? consortiumName : '',
            agencyInclusion: form.agencyType === 'include' ? form.agency?.toUpperCase() || '' : '',
            agencyExclusion: form.agencyType === 'exclude' ? form.agency?.toUpperCase() || '' : '',
            agencyLocationInclusion: form.agencyLocationType === 'include' ? form.agencyLocation?.toUpperCase() || '' : '',
            agencyLocationExclusion: form.agencyLocationType === 'exclude' ? form.agencyLocation?.toUpperCase() || '' : '',
            agentInclusion: form.agentType === 'include' ? form.agent || '' : '',
            agentExclusion: form.agentType === 'exclude' ? form.agent || '' : '',
            bookingChannelInclusion: form.bookingChannel?.length ? JSON.stringify(form.bookingChannel) : '',
            bookingChannelExclusion: '',
            supplierInclusion: form.supplier?.length ?JSON.stringify(form.supplier) : '',
            supplierExclusion: '',
            originInclusion: form.originType === 'include' ? form.origin?.toUpperCase() || '' : '',
            originExclusion: form.originType === 'exclude' ? form.origin?.toUpperCase() || '' : '',
            destinationInclusion: form.destinationType === 'include' ? form.destination?.toUpperCase() || '' : '',
            destinationExclusion: form.destinationType === 'exclude' ? form.destination?.toUpperCase() || '' : '',
            brandedFareInclusion: form.brandCodeType === 'include' ? form.brandCode?.toUpperCase() || '' : '',
            brandedFareExclusion: form.brandCodeType === 'exclude' ? form.brandCode?.toUpperCase() || '' : '',
            airlineInclusion: form.airlineType === 'include' ? form.airline?.toUpperCase() || '' : '',
            airlineExclusion: form.airlineType === 'exclude' ? form.airline.toUpperCase() || '' : '',
            tripTypeInclusion: form.tripTypes?.length ? JSON.stringify(form.tripTypes) : '',
            tripTypeExclusion: '',
            cabinType: form.cabinTypes?.length ? JSON.stringify(form.cabinTypes) : '',
            adAllowCancellation: form.voidAllowed || false,
            voidFees: form.voidFee || '',
            adAllowRefund: form.refundAllowed || false,
            refundFees: form.refundFee || '',
            adAllowReissue: form.reissueAllowed || false,
            reissueFees: form.reissueFee || '',
            ticketingFees: form.ticketingFee || '',
            bookingFees: form.bookingFee || '',
            merchantFees: form.merchantFee || '',
            addOrRemovePUBCommissionAmount: form.pubCommissionAmount || '',
            sellPUBAtCost: form.sellPubAtCost || false,
            lowestOfDefaultMarkuporthisvalue: form.netLowestOfThisOrMarkup || '',
            addOrRemoveFromDefaultMarkup: form.netDefaultMarkup || '',
            promotionCode: form.promotionCode || '',
            loyaltyPoints: form.loyaltyPoints || null,
            serviceFee: form.bookingFee || ''
        };

        if (isLoyalty) {
            payload.overrideNoProfitNoPoints = form.overrideNoProfitNoPoints === true;
        }

        if (salesNaID) {
            payload.naID = salesNaID;
        }

        return payload;
    };




    const getSalespromotion = async (naID) => {
        if (!naID) return undefined;
        if (isAirline) {
            const particularAirlinePromotion = await fetchAirlinePromotionById(naID);
            if (particularAirlinePromotion) {
                reset(mapApiToForm(particularAirlinePromotion));
            }
            return undefined;
        }
        const particularSalesPromotion = await postFetchAllSalesPromotion({ 'naID': naID })
        reset(mapApiToForm(particularSalesPromotion[0]));
        return undefined
    }

    const radioSx = (error) => ({
        color: error ? 'red' : undefined,
        '&.Mui-checked': {
            color: error ? 'red' : undefined,
        },
    });

    useEffect(() => {
        if (!isAirline) {
            getConsortium();
        } else if (salesNaID) {
            getSalespromotion(salesNaID);
        }
    }, [isAirline]);

    useEffect(() => {
        if (!isAirline && consortiumValues?.length && salesNaID) {
            getSalespromotion(salesNaID)
        }
    }, [consortiumValues])

    const getConsortium = async () => {
        await fetch(`${process.env.REACT_APP_BASE_URL}/consortium/all?orgId=${orgId}`, {
            method: "POST",
            redirect: "follow",
        })
            .then((response) => response.json())
            .then((result) => {
                const data = result?.Items?.filter(
                    elem => !elem.consortiumName?.toUpperCase()?.includes("DO NOT USE")
                );
                setConsortiumValues(data || []);
            })
            .catch((error) => console.error("Error: ", error));
    }

    const onSubmit = async (data) => {
        if (isAirline) {
            const payload = removeEmptyStrings(mapFormToApiPayload(data));
            if (salesNaID) {
                await updateAirlinePromotion(salesNaID, payload);
            } else {
                const response = await createAirlinePromotion(payload);
                if (response?.id) {
                    history.push(`${ROUTE_BY_TYPE.airline}/${response.id}`);
                }
            }
            return;
        }

        const payload = isLoyaltyPromotion
            ? removeEmptyStrings(mapFormToApiPayload(data))
            : mapFormToApiPayload(data);
        const response = await updateNewSalesPromotion([payload]);
        if (!salesNaID && response?.naId) {
            const editPrefix = isLoyaltyPromotion ? ROUTE_BY_TYPE.loyalty : ROUTE_BY_TYPE.sales;
            history.push(`${editPrefix}/${response.naId}`);
        }
    };

    const handleDelete = async () => {
        const confirmed = await swal({
            text: 'Are you sure you want to delete this airline promotion?',
            icon: 'warning',
            buttons: ['Cancel', 'Delete'],
            dangerMode: true,
        });
        if (!confirmed || !salesNaID) return;
        const response = await deleteAirlinePromotion(salesNaID);
        if (response?.msg === 'success') {
            history.push(ROUTE_BY_TYPE.airline);
        }
    };

    const today = new Date().toISOString().split('T')[0];
    const promotionTitle = TITLE_BY_TYPE[promotionType];

    return (
        <Card sx={{ width: '100%', mt: 3, padding: '10px 30px' }}>
            <CardHeader title={promotionTitle} />
            <CardContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Typography variant="subtitle1" gutterBottom className="sp-title">Booking Date</Typography>
                    <FormControl component="fieldset" fullWidth>
                        <Controller
                            name="restriction"
                            control={control}
                            render={({ field }) => (
                                <RadioGroup
                                    row
                                    {...field}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        if (value === 'no_restriction') {
                                            setValue('reservationDateFrom', '');
                                            setValue('reservationDateTo', '');
                                        }
                                        field.onChange(e);
                                    }}
                                >
                                    <FormControlLabel value="no_restriction" control={<Radio />} label="No Restriction" />
                                    <FormControlLabel value="specific_duration" control={<Radio />} label="Specific Duration" />
                                    {watch('restriction') === 'specific_duration' && (
                                        <Stack direction="row" spacing={2} mt={2}>
                                            <Controller
                                                name="reservationDateFrom"
                                                control={control}
                                                rules={{
                                                    validate: (value) =>
                                                        watch('restriction') !== 'specific_duration' || value ? true : 'From date is required',
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        type="date"
                                                        label="From"
                                                        InputLabelProps={{ shrink: true }}
                                                        fullWidth
                                                        inputProps={isEdit ? {} : { min: today }}
                                                        size="small"
                                                        error={!!error}
                                                        helperText={error?.message}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="reservationDateTo"
                                                control={control}
                                                rules={{
                                                    validate: (value) => {
                                                        if (watch('restriction') !== 'specific_duration') return true;
                                                        if (!value) return 'To date is required';
                                                        if (watch('reservationDateFrom') && value < watch('reservationDateFrom')) {
                                                            return 'To date must be after or same as From date';
                                                        }
                                                        return true;
                                                    },
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <TextField
                                                        {...field}
                                                        type="date"
                                                        label="To"
                                                        InputLabelProps={{ shrink: true }}
                                                        fullWidth
                                                        inputProps={isEdit ? {} : { min: today }}
                                                        size="small"
                                                        error={!!error}
                                                        helperText={error?.message}
                                                    />
                                                )}
                                            />
                                        </Stack>
                                    )}
                                </RadioGroup>
                            )}
                        />
                    </FormControl>

                    <Typography variant="subtitle1" gutterBottom className="sp-title" sx={{ mt: 3 }}>Departure Date</Typography>
                    <FormControl component="fieldset" fullWidth>
                        <Controller
                            name="departureRestriction"
                            control={control}
                            render={({ field }) => (
                                <RadioGroup
                                    row
                                    {...field}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        if (value === 'no_restriction') {
                                            setValue('departureDateFrom', '');
                                            setValue('departureDateTo', '');
                                        }
                                        field.onChange(e);
                                    }}
                                >
                                    <FormControlLabel value="no_restriction" control={<Radio />} label="No Restriction" />
                                    <FormControlLabel value="specific_duration" control={<Radio />} label="Specific Duration" />
                                    {watch('departureRestriction') === 'specific_duration' && (
                                        <Stack direction="row" spacing={2} mt={2}>
                                            <Controller
                                                name="departureDateFrom"
                                                control={control}
                                                rules={{
                                                    validate: (value) =>
                                                        watch('departureRestriction') !== 'specific_duration' || value ? true : 'From date is required',
                                                }}
                                                render={({ field: depField, fieldState: { error } }) => (
                                                    <TextField
                                                        {...depField}
                                                        type="date"
                                                        label="From"
                                                        InputLabelProps={{ shrink: true }}
                                                        fullWidth
                                                        inputProps={isEdit ? {} : { min: today }}
                                                        size="small"
                                                        error={!!error}
                                                        helperText={error?.message}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="departureDateTo"
                                                control={control}
                                                rules={{
                                                    validate: (value) => {
                                                        if (watch('departureRestriction') !== 'specific_duration') return true;
                                                        if (!value) return 'To date is required';
                                                        if (watch('departureDateFrom') && value < watch('departureDateFrom')) {
                                                            return 'To date must be after or same as From date';
                                                        }
                                                        return true;
                                                    },
                                                }}
                                                render={({ field: depField, fieldState: { error } }) => (
                                                    <TextField
                                                        {...depField}
                                                        type="date"
                                                        label="To"
                                                        InputLabelProps={{ shrink: true }}
                                                        fullWidth
                                                        inputProps={isEdit ? {} : { min: watch('departureDateFrom') || today }}
                                                        size="small"
                                                        error={!!error}
                                                        helperText={error?.message}
                                                    />
                                                )}
                                            />
                                        </Stack>
                                    )}
                                </RadioGroup>
                            )}
                        />
                    </FormControl>

                    {!isAirline && (
                    <>
                    <Grid container spacing={3} mt={3}>
                        {/* Consortium Inclusion/Exclusion */}
                        <Grid item xs={12} md={6}>
                            <Controller name="consortiumType" control={control} defaultValue="include"
                                rules={{
                                    validate: (value) => {
                                        if (watch('consortium')?.trim()) {
                                            return value ? true : "Select one of the above values";
                                        }
                                        return true;
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                    <RadioGroup row {...field}>
                                        <FormControlLabel value="include" control={<Radio sx={radioSx(fieldState.error)} />} label="Include Consortium" />
                                        <FormControlLabel value="exclude" control={<Radio sx={radioSx(fieldState.error)} />} label="Exclude Consortium" />
                                    </RadioGroup>
                                    {fieldState?.error && (
                                        <Typography variant="subtile1" color="error" sx={{ ml: 1 }}>
                                            {fieldState.error.message}
                                        </Typography>
                                    )}
                                    </>
                                )}
                            />

                            <Controller
                                name="consortium"
                                control={control}
                                render={({ field }) => {
                                    const selected = consortiumValues.find(
                                        (option) => option.consortiumId === field.value
                                    ) || null;

                                    return (
                                        <Autocomplete
                                            options={consortiumValues}
                                            getOptionLabel={(option) => option?.consortiumName || ''}
                                            isOptionEqualToValue={(option, value) =>
                                                option?.consortiumId === value?.consortiumId
                                            }
                                            value={selected}
                                            onChange={(_, value) => field.onChange(value?.consortiumId || '')}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Consortium"
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{ mt: 1 }}
                                                    size="small"
                                                />
                                            )}
                                        />
                                    );
                                }}
                            />


                        </Grid>

                        {/* AGENCY LOCATION */}
                        <Grid item xs={12} md={6}>
                            <Controller name="agencyLocationType" control={control} defaultValue="include"
                            rules={{
                                validate: (value) => {
                                    if (watch('agencyLocation')?.trim()) {
                                        return value ? true : "Select one of the above values";
                                    }
                                    return true;
                                },
                            }}
                                render={({ field, fieldState }) => (
                                    <>
                                    <RadioGroup row {...field}>
                                        <FormControlLabel value="include" control={<Radio sx={radioSx(fieldState.error)} />} label="Include Agency Location" />
                                        <FormControlLabel value="exclude" control={<Radio sx={radioSx(fieldState.error)} />} label="Exclude Agency Location" />
                                    </RadioGroup>
                                    {fieldState?.error && (
                                            <Typography variant="subtile1" color="error" sx={{ ml: 1 }}>
                                                {fieldState.error.message}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            />
                            <Controller
                                name="agencyLocation"
                                control={control}
                                rules={{
                                    pattern: {
                                        value: /^[A-Za-z]{2}-[A-Za-z]{2}(,([A-Za-z]{2}-[A-Za-z]{2}))*$/,
                                        message: "Invalid format. Use statecode-countrycode format (e.g., ON-CA, NY-US)"
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <TextField {...field} label="Agency Location" variant="outlined" fullWidth sx={{ mt: 1 }} error={!!fieldState?.error} helperText={fieldState?.error?.message} size="small" />
                                )}
                            />

                        </Grid>
                    </Grid>

                    <Grid container spacing={3} mt={3}>
                        {/* AGENCY */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="agencyType"
                                control={control}
                                defaultValue="include"
                                rules={{
                                    validate: (value) => {
                                        if (watch('agency')?.trim()) {
                                            return value ? true : "Select one of the above values";
                                        }
                                        return true;
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <RadioGroup row {...field}>
                                            <FormControlLabel
                                                value="include"
                                                control={<Radio sx={radioSx(fieldState.error)} />}
                                                label="Include Agency"
                                            />
                                            <FormControlLabel
                                                value="exclude"
                                                control={<Radio sx={radioSx(fieldState.error)} />}
                                                label="Exclude Agency"
                                            />
                                        </RadioGroup>
                                        {fieldState?.error && (
                                            <Typography variant="subtile1" color="error" sx={{ ml: 1 }}>
                                                {fieldState.error.message}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            />
                            <Controller name="agency" control={control}
                                rules={{ pattern: { value: /^[Cc][A-Za-z0-9]{4}(,[Cc][A-Za-z0-9]{4})*$/, message: "Invalid agency codes format. Use 5-letter codes starting with 'C' (e.g., C1234,CEFGH)" } }}
                                render={({ field, fieldState }) => (
                                    <TextField {...field} label="Agency" variant="outlined" fullWidth sx={{ mt: 1 }} error={!!fieldState?.error} helperText={fieldState?.error?.message} size="small" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="agentType" control={control} defaultValue="include"
                                rules={{
                                    validate: (value) => {
                                        if (watch('agent')?.trim()) {
                                            return value ? true : "Select one of the above values";
                                        }
                                        return true;
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                    <RadioGroup row {...field}>
                                        <FormControlLabel value="include" control={<Radio sx={radioSx(fieldState.error)} />} label="Include Agent" />
                                        <FormControlLabel value="exclude" control={<Radio sx={radioSx(fieldState.error)} />} label="Exclude Agent" />
                                    </RadioGroup>
                                    {fieldState?.error && (
                                        <Typography variant="subtile1" color="error" sx={{ ml: 1 }}>
                                            {fieldState.error.message}
                                        </Typography>
                                    )}
                                    </>
                                )}
                            />
                            <Controller name="agent" control={control}
                                render={({ field }) => (
                                    <TextField {...field} label="Agent" variant="outlined" fullWidth sx={{ mt: 1 }} size="small" />
                                )}
                            />
                        </Grid>
                    </Grid>
                    </>
                    )}

                    <Grid container spacing={3} mt={3}>
                        {/* Origin */}
                        <Grid item xs={12} md={6}>
                            {!isAirline && (
                            <Controller name="originType" control={control} defaultValue="include"
                            rules={{
                                validate: (value) => {
                                    if (watch('origin')?.trim()) {
                                        return value ? true : "Select one of the above values";
                                    }
                                    return true;
                                },
                            }}
                                render={({ field, fieldState }) => (
                                    <>
                                    <RadioGroup row {...field}>
                                        <FormControlLabel value="include" control={<Radio sx={radioSx(fieldState.error)} />} label="Include Origin" />
                                        <FormControlLabel value="exclude" control={<Radio sx={radioSx(fieldState.error)} />} label="Exclude Origin" />
                                    </RadioGroup>
                                    {fieldState?.error && (
                                            <Typography variant="subtile1" color="error" sx={{ ml: 1 }}>
                                                {fieldState.error.message}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            />
                            )}
                            <Controller name="origin" control={control}
                                rules={{ pattern: { value: /^([A-Za-z]{3}|[A-Za-z]{2}|\d)(,([A-Za-z]{3}|[A-Za-z]{2}|\d))*$/, message: "Invalid origin codes format. Use 3-letter airport codes, 2-letter country codes, or 1-digit IATA codes" } }}
                                render={({ field, fieldState }) => (
                                    <TextField {...field} label="Origin" variant="outlined" fullWidth sx={{ mt: 1 }} error={!!fieldState?.error} helperText={fieldState?.error?.message} size="small" />
                                )}
                            />
                        </Grid>
                        {/* Destination */}
                        <Grid item xs={12} md={6}>
                            {!isAirline && (
                            <Controller name="destinationType" control={control} defaultValue="include"
                                rules={{
                                    validate: (value) => {
                                        if (watch('destination')?.trim()) {
                                            return value ? true : "Select one of the above values";
                                        }
                                        return true;
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                    <RadioGroup row {...field}>
                                        <FormControlLabel value="include" control={<Radio sx={radioSx(fieldState.error)} />} label="Include Destination" />
                                        <FormControlLabel value="exclude" control={<Radio sx={radioSx(fieldState.error)} />} label="Exclude Destination" />
                                    </RadioGroup>
                                    {fieldState?.error && (
                                            <Typography variant="subtile1" color="error" sx={{ ml: 1 }}>
                                                {fieldState.error.message}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            />
                            )}
                            <Controller name="destination" control={control} rules={{ pattern: { value: /^([A-Za-z]{3}|[A-Za-z]{2}|\d)(,([A-Za-z]{3}|[A-Za-z]{2}|\d))*$/, message: "Invalid destination codes format. Use 3-letter airport codes, 2-letter country codes, or 1-digit IATA codes" } }}
                                render={({ field, fieldState }) => (
                                    <TextField {...field} label="Destination" variant="outlined" fullWidth sx={{ mt: 1 }} error={!!fieldState?.error} helperText={fieldState?.error?.message} size="small" />
                                )}
                            />

                        </Grid>
                    </Grid>
                    <Grid container spacing={3} mt={3}>
                        {!isAirline && (
                        <Grid item xs={12} md={6}>
                            <Controller name="brandCodeType" control={control} defaultValue="include"
                                rules={{
                                    validate: (value) => {
                                        if (watch('brandCode')?.trim()) {
                                            return value ? true : "Select one of the above values";
                                        }
                                        return true;
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                    <RadioGroup row {...field}>
                                        <FormControlLabel value="include" control={<Radio sx={radioSx(fieldState.error)} />} label="Include Brand Code" />
                                        <FormControlLabel value="exclude" control={<Radio sx={radioSx(fieldState.error)} />} label="Exclude Brand Code" />
                                    </RadioGroup>
                                    {fieldState?.error && (
                                        <Typography variant="subtile1" color="error" sx={{ ml: 1 }}>
                                            {fieldState.error.message}
                                        </Typography>
                                    )}
                                    </>
                                )}
                            />
                            <Controller name="brandCode" control={control}
                                render={({ field }) => (
                                    <TextField {...field} label="Brand Code" variant="outlined" fullWidth sx={{ mt: 1 }} size="small" />
                                )}
                            />
                        </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                            {!isAirline && (
                            <Controller name="airlineType" control={control} defaultValue="include"
                                rules={{
                                    validate: (value) => {
                                        if (watch('airline')?.trim()) {
                                            return value ? true : "Select one of the above values";
                                        }
                                        return true;
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                    <RadioGroup row {...field}>
                                        <FormControlLabel value="include" control={<Radio sx={radioSx(fieldState.error)} />} label="Include Airline" />
                                        <FormControlLabel value="exclude" control={<Radio sx={radioSx(fieldState.error)} />} label="Exclude Airline" />
                                    </RadioGroup>
                                    {fieldState?.error && (
                                        <Typography variant="subtile1" color="error" sx={{ ml: 1 }}>
                                            {fieldState.error.message}
                                        </Typography>
                                    )}
                                    </>
                                )}
                            />
                            )}
                            <Controller name="airline" control={control} defaultValue="" rules={{ pattern: { value: /^[A-Za-z0-9]{2}(,[A-Za-z0-9]{2})*$/, message: "Please enter a valid airline code (e.g., AA,BA,DL)" } }}
                                render={({ field, fieldState }) => (<TextField {...field} label="Airline" variant="outlined" fullWidth sx={{ mt: 1 }} error={!!fieldState?.error} helperText={fieldState?.error?.message} size="small" />)}
                            />
                        </Grid>
                    </Grid>
                    {!isAirline && (
                    <Grid item xs={12}>
                        <Typography gutterBottom className='sp-title mr-4 mt-4 d-flex align-items-center'>Booking Channel</Typography>
                        <Controller name="bookingChannel" control={control} defaultValue={[]}
                            render={({ field }) => {
                                const handleChange = (event) => {
                                    const { value } = event.target;
                                    const currentIndex = field.value.indexOf(value);
                                    const newChecked = [...field.value];
                                    if (currentIndex === -1) {
                                        newChecked.push(value);
                                    } else {
                                        newChecked.splice(currentIndex, 1);
                                    }
                                    field.onChange(newChecked);
                                };

                                return (
                                    <FormControl component="fieldset">
                                        <FormGroup row>
                                            <FormControlLabel control={<Checkbox checked={field.value.includes('Web')} onChange={handleChange} value="Web" />} label="Online" />
                                            <FormControlLabel control={<Checkbox checked={field.value.includes('CallCenter')} onChange={handleChange} value="CallCenter" />} label="Call Center" />
                                        </FormGroup>
                                    </FormControl>
                                );
                            }}
                        />
                    </Grid>
                    )}


                    <Grid item xs={12}>
                        <Typography gutterBottom className='sp-title mr-4 d-flex align-items-center'>Supplier</Typography>

                        <Controller name="supplier" control={control} defaultValue={[]}
                            render={({ field }) => {
                                const handleChange = (event) => {
                                    const { value } = event.target;
                                    const currentIndex = field.value.indexOf(value);
                                    const newChecked = [...field.value];

                                    if (currentIndex === -1) {
                                        newChecked.push(value);
                                    } else {
                                        newChecked.splice(currentIndex, 1);
                                    }

                                    field.onChange(newChecked);
                                };

                                return (
                                    <FormControl component="fieldset">
                                        <FormGroup row>
                                            <FormControlLabel control={<Checkbox checked={field.value.includes('Sabre')} onChange={handleChange} value="Sabre" />} label="Sabre" />
                                            {/* <FormControlLabel control={<Checkbox checked={field.value.includes('NDC')} onChange={handleChange} value="NDC" />} label="Duffel" /> */}
                                            <FormControlLabel control={<Checkbox checked={field.value.includes('Sabre_NDC')} onChange={handleChange} value="Sabre_NDC" />} label="Sabre NDC" />
                                            <FormControlLabel control={<Checkbox checked={field.value.includes('Amadeus_NDC')} onChange={handleChange} value="Amadeus_NDC" />} label="Amadeus NDC" />
                                            {/* <FormControlLabel control={<Checkbox checked={field.value.includes('AC_NDC')} onChange={handleChange} value="AC_NDC" />} label="AC NDC" /> */}
                                            <FormControlLabel control={<Checkbox checked={field.value.includes('HS_NDC_Aggregator')} onChange={handleChange} value="HS_NDC_Aggregator" />} label="HS NDC Aggregator" />
                                        </FormGroup>
                                    </FormControl>
                                );
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Typography gutterBottom className='sp-title mr-4 mt-4 d-flex align-items-center'>Trip Type</Typography>

                        <Controller name="tripTypes" control={control} defaultValue={[]}
                            render={({ field }) => (
                                <FormGroup row>
                                    <FormControlLabel
                                        control={
                                            <Checkbox checked={field.value.includes('OW')} onChange={(e) => { const checked = e.target.checked; const value = 'OW'; field.onChange(checked ? [...field.value, value] : field.value.filter((item) => item !== value)); }} />
                                        }
                                        label="One Way"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox checked={field.value.includes('RT')} onChange={(e) => { const checked = e.target.checked; const value = 'RT'; field.onChange(checked ? [...field.value, value] : field.value.filter((item) => item !== value)); }} />
                                        }
                                        label="Round Trip"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox checked={field.value.includes('OJ')} onChange={(e) => { const checked = e.target.checked; const value = 'OJ'; field.onChange(checked ? [...field.value, value] : field.value.filter((item) => item !== value)); }} />
                                        }
                                        label="Open Jaw"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox checked={field.value.includes('MC')} onChange={(e) => { const checked = e.target.checked; const value = 'MC'; field.onChange(checked ? [...field.value, value] : field.value.filter((item) => item !== value)); }} />
                                        }
                                        label="Multi City"
                                    />
                                </FormGroup>
                            )}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography gutterBottom className='sp-title mr-4 mt-4 d-flex align-items-center'>Cabin Type</Typography>
                        <Controller
                            name="cabinTypes"
                            control={control}
                            defaultValue={[]}
                            render={({ field }) => (
                                <FormGroup row>
                                    <FormControlLabel
                                        control={
                                            <Checkbox checked={field.value.includes('Economy')} onChange={(e) => { const checked = e.target.checked; const value = 'Economy'; field.onChange(checked ? [...field.value, value] : field.value.filter((item) => item !== value)); }} />
                                        }
                                        label="Economy"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox checked={field.value.includes('PremiumEconomy')} onChange={(e) => { const checked = e.target.checked; const value = 'PremiumEconomy'; field.onChange(checked ? [...field.value, value] : field.value.filter((item) => item !== value)); }} />
                                        }
                                        label="Premium Economy"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox checked={field.value.includes('Business')} onChange={(e) => { const checked = e.target.checked; const value = 'Business'; field.onChange(checked ? [...field.value, value] : field.value.filter((item) => item !== value)); }} />
                                        }
                                        label="Business"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox checked={field.value.includes('First')} onChange={(e) => { const checked = e.target.checked; const value = 'First'; field.onChange(checked ? [...field.value, value] : field.value.filter((item) => item !== value)); }} />
                                        }
                                        label="First"
                                    />
                                </FormGroup>
                            )}
                        />
                    </Grid>

                    {(isLoyalty || isAirline) ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography gutterBottom className='sp-title mr-4 d-flex align-items-center'>Promotion Code</Typography>
                                <Controller
                                    name="promotionCode"
                                    control={control}
                                    rules={{ required: 'Promotion Code is required' }}
                                    render={({ field, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            label="Promotion Code"
                                            variant="outlined"
                                            fullWidth
                                            error={!!error}
                                            helperText={error?.message}
                                            size="small"
                                        />
                                    )}
                                />
                            </Grid>
                            {!isAirline && (
                            <Grid item xs={12} md={6}>
                                <Typography gutterBottom className='sp-title mr-4 d-flex align-items-center'>Loyalty Points</Typography>
                                <Controller
                                    name="loyaltyPoints"
                                    control={control}
                                    rules={{
                                        required: 'Loyalty Points is required',
                                        pattern: {
                                            value: /^[0-9]+$/,
                                            message: 'Loyalty Points must be a number',
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            label="Loyalty Points"
                                            variant="outlined"
                                            fullWidth
                                            error={!!error}
                                            helperText={error?.message}
                                            size="small"
                                        />
                                    )}
                                />
                            </Grid>
                            )}
                            {isLoyalty && (
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="overrideNoProfitNoPoints"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name={field.name}
                                                    inputRef={field.ref}
                                                    checked={field.value === true}
                                                    onBlur={field.onBlur}
                                                    onChange={(e) => field.onChange(e.target.checked)}
                                                />
                                            }
                                            label="Override No profit No points"
                                            className="mt-4"
                                        />
                                    )}
                                />
                            </Grid>
                            )}
                        </Grid>
                    ) : isSales ? (
                        <>
                    <Grid item xs={12} md={6}>
                        <Typography gutterBottom className='sp-title mr-4 mt-4 d-flex align-items-center'>Promotional Values</Typography>
                        <FormGroup row>
                            <Controller name="voidAllowed" control={control} defaultValue={false}
                                render={({ field }) => (
                                    <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Void Allowed" />
                                )}
                            />
                            <Controller name="refundAllowed" control={control} defaultValue={false}
                                render={({ field }) => (
                                    <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Refund Allowed" />
                                )}
                            />
                            <Controller name="reissueAllowed" control={control} defaultValue={false}
                                render={({ field }) => (
                                    <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Reissue Allowed" />
                                )}
                            />
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography gutterBottom className='sp-title mr-4 mt-4 d-flex align-items-center'>Fees</Typography>
                        <Stack direction="row" spacing={2}>
                            <Controller
                                name="ticketingFee"
                                control={control}
                                defaultValue=""
                                rules={{
                                    pattern: {
                                        value: /^$|^[+-]?\d+(\.\d+)?$/, // Allow empty or valid number with optional +/- at the start
                                        message: "Please enter a valid number with optional +/-"
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <Grid container direction="column">
                                            <Grid item>
                                                <Typography gutterBottom className="sp-title"> Ticketing Fee </Typography>
                                            </Grid>
                                            <Grid item>
                                                <TextField {...field} label="Ticketing Fee" variant="outlined" size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} />
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            />
                            <Controller
                                name="voidFee"
                                control={control}
                                defaultValue=""
                                rules={{
                                    pattern: {
                                        value: /^$|^[+-]?\d+(\.\d+)?$/, // Allow empty or valid number with optional +/- at the start
                                        message: "Please enter a valid number with optional +/-"
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <Grid container direction="column">
                                            <Grid item>
                                                <Typography gutterBottom className="sp-title"> Void Fee </Typography>
                                            </Grid>
                                            <Grid item>
                                                <TextField {...field} label="Void Fee" variant="outlined" size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} />
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            />
                            <Controller
                                name="refundFee"
                                control={control}
                                defaultValue=""
                                rules={{
                                    pattern: {
                                        value: /^$|^[+-]?\d+(\.\d+)?$/, // Allow empty or valid number with optional +/- at the start
                                        message: "Please enter a valid number with optional +/-"
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <>  <Grid container direction="column">
                                        <Grid item>
                                            <Typography gutterBottom className="sp-title"> Refund Fee </Typography>
                                        </Grid>
                                        <Grid item>
                                            <TextField {...field} label="Refund Fee" variant="outlined" size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} />
                                        </Grid>
                                    </Grid>
                                    </>
                                )}
                            />
                            <Controller
                                name="reissueFee"
                                control={control}
                                defaultValue=""
                                rules={{
                                    pattern: {
                                        value: /^$|^[+-]?\d+(\.\d+)?$/, // Allow empty or valid number with optional +/- at the start
                                        message: "Please enter a valid number with optional +/-"
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <Grid container direction="column">
                                            <Grid item>
                                                <Typography gutterBottom className="sp-title"> Reissue Fee </Typography>
                                            </Grid>
                                            <Grid item>
                                                <TextField {...field} label="Reissue Fee" variant="outlined" size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} />
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            />
                            <Controller
                                name="bookingFee"
                                control={control}
                                defaultValue=""
                                rules={{
                                    pattern: {
                                        value: /^$|^[+-]?\d+(\.\d+)?$/, // Allow empty or valid number with optional +/- at the start
                                        message: "Please enter a valid number with optional +/-"
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <Grid container direction="column">
                                            <Grid item>
                                                <Typography gutterBottom className="sp-title"> Booking Fee </Typography>
                                            </Grid>
                                            <Grid item>
                                                <TextField {...field} label="Booking Fee" variant="outlined" size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} />
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            />
                            <Controller
                                name="merchantFee"
                                control={control}
                                defaultValue=""
                                rules={{
                                    pattern: {
                                        value: /^$|^[+-]?\d+(\.\d+)?$/, // Allow empty or valid number with optional +/- at the start
                                        message: "Please enter a valid number with optional +/-"
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <Grid container direction="column">
                                            <Grid item>
                                                <Typography gutterBottom className="sp-title"> Merchant Fee </Typography>
                                            </Grid>
                                            <Grid item>
                                                <TextField {...field} label="Merchant Fee" variant="outlined" size="small" error={!!fieldState?.error} helperText={fieldState?.error?.message} />
                                            </Grid>
                                        </Grid>

                                    </>
                                )}
                            />
                        </Stack>
                    </Grid>
                    {/* Commission / Markup Section */}
                    <Grid item xs={12}>
                        <Typography gutterBottom className="sp-title mt-4"> Commission / Markup </Typography>
                        <Grid container spacing={4}>
                            {/* Pub Fare Section */}
                            <Grid item xs={12} md={6}>
                                <Typography gutterBottom className="sp-title"> Pub Fare </Typography>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={6}>
                                        <Typography gutterBottom className="sp-title"> +/- Commission Amount </Typography>
                                        <Controller
                                            name="pubCommissionAmount"
                                            control={control}
                                            defaultValue=""
                                            rules={{
                                                pattern: {
                                                    value: /^$|^[+-]?\$?\d+(\.\d+)?$/,
                                                    message: "Please enter a valid number with optional +/-",
                                                },
                                            }}
                                            render={({ field, fieldState }) => (
                                                <TextField
                                                    {...field}
                                                    label="+/- Commission Amount"
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    error={!!fieldState?.error}
                                                    helperText={fieldState?.error?.message}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="sellPubAtCost"
                                            control={control}
                                            defaultValue={false}
                                            render={({ field }) => (
                                                <FormControlLabel
                                                    control={<Checkbox {...field} checked={field.value} />}
                                                    label="Sell PUB at cost"
                                                    className='mt-4'
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Net Fare Section */}
                            <Grid item xs={12} md={6}>
                                <Typography gutterBottom className="sp-title"> Net Fare </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography gutterBottom className="sp-title"> Lowest of: this or markup </Typography>
                                        <Controller
                                            name="netLowestOfThisOrMarkup"
                                            control={control}
                                            defaultValue=""
                                            rules={{
                                                pattern: {
                                                    value: /^$|^[+-]?\d+(\.\d+)?$/,
                                                    message: "Please enter a valid number with optional +/-",
                                                },
                                            }}
                                            render={({ field, fieldState }) => (
                                                <TextField {...field} label="Lowest of: this or markup" variant="outlined" size="small" fullWidth error={!!fieldState?.error} helperText={fieldState?.error?.message} />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography gutterBottom className="sp-title"> +/- Default Markup </Typography>
                                        <Controller name="netDefaultMarkup" control={control} defaultValue="" rules={{ pattern: { value: /^$|^[+-]?\d+(\.\d+)?$/, message: "Please enter a valid number with optional +/-", }, }}
                                            render={({ field, fieldState }) => (
                                                <TextField {...field} label="+/- Default Markup" variant="outlined" size="small" fullWidth error={!!fieldState?.error} helperText={fieldState?.error?.message} />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                        </>
                    ) : null}
                    {Object.keys(errors).length > 0 && (
                        <Typography color="error" sx={{ mt: 2 }}>
                        Please fill in all required fields correctly.
                        </Typography>
                    )}
                    <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                        <Button type="submit" variant="contained">Submit</Button>
                        {isAirline && isEdit && (
                            <Button type="button" variant="outlined" color="error" onClick={handleDelete}>
                                Delete
                            </Button>
                        )}
                    </Stack>
                </form>
            </CardContent>
        </Card>
    );
}

export default PromotionEdit;
